import { useCallback, useMemo } from "react";
import { nanoid as generateId } from "src/util/simpleId";
import Consts from "../consts";
import { useUserPreferences } from "./useUserPreferences";

const isValidView = (view: unknown): view is user_prefs.TeamCanvasView =>
  Boolean(
    view &&
    typeof view === "object" &&
    typeof (view as user_prefs.TeamCanvasView).id === "string" &&
    (view as user_prefs.TeamCanvasView).id &&
    typeof (view as user_prefs.TeamCanvasView).name === "string" &&
    (view as user_prefs.TeamCanvasView).name.trim() &&
    Array.isArray((view as user_prefs.TeamCanvasView).teamIds) &&
    (view as user_prefs.TeamCanvasView).teamIds.length > 0
  );

const isValidCollection = (
  collection: unknown
): collection is user_prefs.TeamCanvasViewCollection =>
  Boolean(
    collection &&
    typeof collection === "object" &&
    typeof (collection as user_prefs.TeamCanvasViewCollection).id ===
      "string" &&
    (collection as user_prefs.TeamCanvasViewCollection).id &&
    typeof (collection as user_prefs.TeamCanvasViewCollection).name ===
      "string" &&
    (collection as user_prefs.TeamCanvasViewCollection).name.trim() &&
    Array.isArray(
      (collection as user_prefs.TeamCanvasViewCollection).viewIds
    ) &&
    (collection as user_prefs.TeamCanvasViewCollection).viewIds.length > 0
  );

const uniqueTruthyIds = (ids: string[]): string[] => [
  ...new Set((ids || []).filter(Boolean)),
];

/** Saved Team Canvas views (name + team ids) and ordered presentation collections, per workspace user. */
export const useUserTeamCanvasViews = () => {
  const {
    loading,
    mergedPreferences,
    workspaceUserPreferences,
    updatePreferencesForWorkspaceUser,
  } = useUserPreferences();

  const views = useMemo(
    () =>
      (
        mergedPreferences?.teamCanvasViews ||
        Consts.defaultPreferences.teamCanvasViews
      ).filter(isValidView),
    [mergedPreferences?.teamCanvasViews]
  );

  const collections = useMemo(
    () =>
      (
        mergedPreferences?.teamCanvasViewCollections ||
        Consts.defaultPreferences.teamCanvasViewCollections
      ).filter(isValidCollection),
    [mergedPreferences?.teamCanvasViewCollections]
  );

  const updateTeamCanvasPreferences = useCallback(
    ({
      nextViews = views,
      nextCollections = collections,
    }: {
      nextViews?: user_prefs.TeamCanvasViews;
      nextCollections?: user_prefs.TeamCanvasViewCollections;
    }): Promise<boolean> =>
      updatePreferencesForWorkspaceUser({
        ...workspaceUserPreferences,
        teamCanvasViews: nextViews,
        teamCanvasViewCollections: nextCollections,
      }),
    [
      collections,
      updatePreferencesForWorkspaceUser,
      views,
      workspaceUserPreferences,
    ]
  );

  const saveView = useCallback(
    async ({
      name,
      teamIds,
    }: {
      name: string;
      teamIds: string[];
    }): Promise<user_prefs.TeamCanvasView | null> => {
      const view: user_prefs.TeamCanvasView = {
        id: generateId(),
        name: (name || "").trim(),
        teamIds: (teamIds || []).filter(Boolean),
        createdAt: new Date().toISOString(),
      };
      if (!isValidView(view)) {
        return null;
      }
      const saved = await updateTeamCanvasPreferences({
        nextViews: [...views, view],
      });
      return saved ? view : null;
    },
    [views, updateTeamCanvasPreferences]
  );

  const deleteView = useCallback(
    (viewId: string): Promise<boolean> => {
      const nextViews = views.filter((view) => view.id !== viewId);
      const nextCollections = collections
        .map((collection) => ({
          ...collection,
          viewIds: collection.viewIds.filter((id) => id !== viewId),
          updatedAt: new Date().toISOString(),
        }))
        .filter(isValidCollection);
      return updateTeamCanvasPreferences({ nextViews, nextCollections });
    },
    [collections, views, updateTeamCanvasPreferences]
  );

  const saveCollection = useCallback(
    async ({
      id,
      name,
      viewIds,
    }: {
      id?: string;
      name: string;
      viewIds: string[];
    }): Promise<user_prefs.TeamCanvasViewCollection | null> => {
      const now = new Date().toISOString();
      const existing = id
        ? collections.find((collection) => collection.id === id)
        : null;
      const collection: user_prefs.TeamCanvasViewCollection = {
        id: existing?.id || generateId(),
        name: (name || "").trim(),
        viewIds: uniqueTruthyIds(viewIds),
        createdAt: existing?.createdAt || now,
        updatedAt: now,
      };
      if (!isValidCollection(collection)) {
        return null;
      }
      const nextCollections = existing
        ? collections.map((item) =>
            item.id === collection.id ? collection : item
          )
        : [...collections, collection];
      const saved = await updateTeamCanvasPreferences({ nextCollections });
      return saved ? collection : null;
    },
    [collections, updateTeamCanvasPreferences]
  );

  const deleteCollection = useCallback(
    (collectionId: string): Promise<boolean> =>
      updateTeamCanvasPreferences({
        nextCollections: collections.filter(
          (collection) => collection.id !== collectionId
        ),
      }),
    [collections, updateTeamCanvasPreferences]
  );

  return {
    loading,
    views,
    collections,
    saveView,
    deleteView,
    saveCollection,
    deleteCollection,
  };
};
